// Libs
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Steps, Col, Row, Tooltip } from 'antd';
import { FormikProps } from 'formik';

// Own Libs
import * as ui from '@digital/ui';

//Api
import PerfectScrollbar from 'react-perfect-scrollbar';

//Files
import { Visibility } from '@app/components';
import './style.css';
import { ConfigStep, IGroupItemStep, IItemStep, V2StepPanelProps } from './interfaces';

//Utils

function V2StepPanel({
  groupSteps,
  header,
  contentLegend,
  direction = 'vertical',
  numberCurrentStep,
  onNext,
  onPrev,
  actions,
  showNext = true,
  showPrev = true,
  onChangeStep,
  onChangeStepOrdinal,
  showAllActions = true,
}: V2StepPanelProps) {

  const [activeOrdinal, setActiveOrdinal] = useState<number>(0);
  const [allSteps, setAllSteps] = useState<IItemStep[]>([]);

  useEffect(() => {
    let steps: IItemStep[] = [];

    let count = 0;
    groupSteps
      .sort((a, b) => a.order - b.order)
      .forEach((group: IGroupItemStep) => {
        group.steps.forEach((step: IItemStep) => {

          if (step.visible == true) {
            step.order = count;
            steps.push(step);
            count++;
          }

        });
      });

    setAllSteps(steps);
  }, [groupSteps]);

  const [config, setConfig] = useState<ConfigStep>({
    stepsWidth: '300px',
    stepSpan: 6,
    stepContentSpan: 18,
  });

  useEffect(() => {
    if (direction === 'horizontal') {
      setConfig({
        stepsWidth: '100%',
        stepSpan: 24,
        stepContentSpan: 24,
      });
    }
  }, [direction]);



  useEffect(() => {
    let step = getCurrentStep();
    step && onChangeStep && onChangeStep(step);
    onChangeStepOrdinal && onChangeStepOrdinal(activeOrdinal);
  }, [activeOrdinal]);

  useEffect(() => {
    if (numberCurrentStep) {
      let step = getStepByNumber(numberCurrentStep);
      let order = step && step.order || 0;
      if (order > 0) {
        setActiveOrdinal(order);
      }
    }
  }, [numberCurrentStep, allSteps]);

  const nextStep = (values: any, formProps: FormikProps<any>) => {
    let step = getCurrentStep();
    step && onNext && onNext(step, activeOrdinal)
      .then((result) => {
        if (result) {
          if (activeOrdinal < allSteps.length - 1) {
            setActiveOrdinal(activeOrdinal + 1);
          }
        }
      });
  };

  const previuosStep = () => {
    let referenceStep = getCurrentStep(activeOrdinal - 1);
    referenceStep && onPrev && onPrev(referenceStep, activeOrdinal, allSteps)
      .then((result) => {
        if (result) {
          if (activeOrdinal > 0) {
            setActiveOrdinal(activeOrdinal - 1);
          }
        }
      });
  };

  const getStepByNumber = (num: number) => allSteps.find((item) => item.stepNumber === num);

  const getCurrentStep = (order?: number): IItemStep | undefined => {
    if (order === undefined) {
      order = activeOrdinal;
    }
    return order >= 0 && order < allSteps.length
      ? allSteps.find((item) => item.order === order)
      : undefined;
  };

  const renderSteps = (key: number, steps: IItemStep[], init: number) => (
    <Steps
      key={key + Math.random()}
      initial={key != 0 ? init : 0}
      current={activeOrdinal}
      size="small"
      style={{ maxWidth: config.stepsWidth }}
      direction={direction}>
      {steps.map((step) => (
        <Steps.Step key={(step.order || 0) + Math.random()} title={step.title} description={step.description} {...(step.status ? { status: step.status } : {})} />
      ))}
    </Steps>
  );


  const renderSidebar = () => useMemo(() => {
    let continueIndex = 0;

    return groupSteps.map((group, index) => {
      let steps = group.steps.filter((step) => step.visible === true) as IItemStep[];

      let contentTemp = (
        group.title ? (
          <fieldset key={group.title}>
            <legend id={`mrn-legend-stepv2-sidebar-${group.title}`}>{group.title}</legend>
            {renderSteps(index, steps, continueIndex)}
          </fieldset>
        ) :
          renderSteps(index, steps, continueIndex)
      );

      continueIndex += steps.length;

      return contentTemp;
    });
  }, [groupSteps, activeOrdinal]);


  const renderContent = () => {
    let step = getCurrentStep();

    if (step && step.visible == true) {
      return contentLegend ? (
        <fieldset>
          <legend id={`mrn-legend-stepv2-content`}>{contentLegend}</legend>
          <>
            {step.content}
          </>
        </fieldset>
      ) : (step.content);
    }
    return <p>Conteúdo Indisponível</p>;
  };

  const actionsMemo = (): React.ReactNode[] => {
    const actionsTemp: React.ReactNode[] = [];

    let step = getCurrentStep();

    if (showAllActions && actions) {
      actionsTemp.push(...actions);
    }

    if (showAllActions && showPrev && activeOrdinal > 0 || (step && step.stepFrontendControl)) {
      actionsTemp.push(
        <ui.Button
          id='mrn-button-stepv2-prev'
          key="prev"
          type="primary"
          onClick={previuosStep}>
          Voltar
        </ui.Button>
      );
    }
    
    

    if (showAllActions && showNext && activeOrdinal < allSteps.length - 1) {

      if (step && !step.stepFrontendControl) {
        actionsTemp.push(
          <Tooltip title="Próximo">
            <ui.FormSubmitButton
              id='mrn-button-stepv2-next'
              noIcon
              type="success"
            >
              <ui.FormatText id="Próximo" uppercase />
            </ui.FormSubmitButton>
          </Tooltip>
        );
      } else {
        actionsTemp.push(
          <Tooltip title="Próximo">
            <ui.FormSubmitButton
              id='mrn-button-stepv2-next'
              noIcon
              type="success"
              onClick={nextStep}
            >
              <ui.FormatText id="Próximo" uppercase />
            </ui.FormSubmitButton>
          </Tooltip>
        );
      }
    }

    return actionsTemp;
  };

  return (
    <ui.Widget>
      <Visibility
        key="V2StepPanel"
        visible={allSteps.length > 0}
      >
        <Row gutter={24}>
          <Col span={config.stepSpan} style={{ height: '100vh' }}>
            <PerfectScrollbar>
              {renderSidebar()}
            </PerfectScrollbar>
          </Col>

          <Col span={config.stepContentSpan}>
            {header && header()}
            <ui.Widget
              actions={actionsMemo()}
              style={{
                border: 'none',
                boxShadow: 'none',
                zIndex: 99
              }
              }
            />
            {renderContent()}
          </Col>
        </Row>
      </Visibility>
    </ui.Widget>
  );
}

export default memo(V2StepPanel);
